import { useCallback, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { importApi } from '../api/imports'
import type { EntityDef } from '../api/types'
import { useCompanies } from '../companies/useCompanies'
import { ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { MODIFY_DATE_PRESETS, ModifyWizardContext } from './ModifyWizardContext'
import type { ModifyDateMode, ModifyWizard } from './ModifyWizardContext'

/**
 * Holds the modify wizard's choices between steps. Nothing is persisted here;
 * the edit step submits the changed records to `POST /api/modify/submit`, which
 * queues a job that writes them back to QuickBooks.
 *
 * The entity list comes from `GET /api/import/entities` — the same trade-off
 * Export documents: there is no modify-specific entities endpoint, and that one
 * already carries the `isTransaction` flag the Lists / Transactions toggle needs.
 */
export function ModifyWizardProvider() {
  const entities = useAsync<EntityDef[]>(() => importApi.entities(), [])
  const { current } = useCompanies()
  const [entityType, setEntityTypeState] = useState('')
  const [reference, setReference] = useState('')
  const [dateMode, setDateMode] = useState<ModifyDateMode>('preset')
  const [preset, setPreset] = useState(MODIFY_DATE_PRESETS[0])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  // Changing the subject invalidates the filters that were scoped to it.
  const setEntityType = useCallback((name: string) => {
    setEntityTypeState(name)
    setReference('')
  }, [])

  const reset = useCallback(() => {
    setEntityTypeState('')
    setReference('')
    setDateMode('preset')
    setPreset(MODIFY_DATE_PRESETS[0])
    setFrom('')
    setTo('')
  }, [])

  const list = useMemo(() => entities.data ?? [], [entities.data])
  const value = useMemo<ModifyWizard>(
    () => ({
      entities: list,
      entityType,
      entity: list.find((e) => e.name === entityType) ?? null,
      companyId: current?.id ?? null,
      reference,
      dateMode,
      preset,
      from,
      to,
      setEntityType,
      setReference,
      setDateMode,
      setPreset,
      setFrom,
      setTo,
      reset,
    }),
    [list, entityType, current?.id, reference, dateMode, preset, from, to, setEntityType, reset],
  )

  if (entities.loading) return <LoadingState label="Loading modify types…" />
  if (entities.error) return <ErrorState error={entities.error} onRetry={entities.reload} />

  return (
    <ModifyWizardContext.Provider value={value}>
      <Outlet />
    </ModifyWizardContext.Provider>
  )
}
